import { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import Button from '../../reusable/Button/Button';
import {
  useGetTwitterProfilesQuery,
  useUpdateTwitterProfilesMutation,
} from '../../store/api/trademateApi';
import { t } from '../../i18n/i18n';

const WIDGETS_SRC = 'https://platform.twitter.com/widgets.js';

/** X's embed script, loaded once for the session and shared by every timeline. */
let widgets = null;
const loadWidgets = () => {
  widgets ??= new Promise((resolve, reject) => {
    if (window.twttr?.widgets) return resolve(window.twttr);
    const script = document.createElement('script');
    script.async = true;
    script.src = WIDGETS_SRC;
    script.onload = () => window.twttr?.ready?.(resolve) ?? resolve(window.twttr);
    script.onerror = () => {
      widgets = null;
      reject(new Error('widgets.js blocked'));
    };
    document.head.appendChild(script);
  });
  return widgets;
};

/** "@Alpha_Capital", "x.com/Alpha_Capital" or "Alpha_Capital" — the handle alone. */
const handleOf = (input) =>
  input
    .trim()
    .replace(/^https?:\/\/(www\.)?(twitter|x)\.com\//i, '')
    .replace(/^@/, '')
    .split(/[/?#]/)[0];

const VALID_HANDLE = /^\w{1,15}$/;

/** One profile's timeline, drawn by X's script into its own box. */
function Timeline({ screenName }) {
  const ref = useRef(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    const box = ref.current;
    let live = true;
    setState('loading');
    loadWidgets()
      .then((twttr) =>
        twttr.widgets.createTimeline({ sourceType: 'profile', screenName }, box, {
          theme: 'dark',
          height: 560,
          chrome: 'noheader nofooter noborders transparent',
          dnt: true,
        }),
      )
      // X resolves with nothing when it won't draw the timeline — which, since
      // it closed embedded timelines to signed-out viewers, is often.
      .then((el) => live && setState(el ? 'ready' : 'empty'))
      .catch(() => live && setState('empty'));
    return () => {
      live = false;
      box.replaceChildren();
    };
  }, [screenName]);

  return (
    <article className="tm-tweets__card">
      <a
        className="tm-tweets__name"
        href={`https://x.com/${screenName}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        @{screenName}
      </a>
      <div ref={ref} className="tm-tweets__timeline" />
      {state !== 'ready' && (
        <p className="tm-tweets__note">
          {state === 'loading' ? t('Loading posts…') : `${t('X didn’t load this timeline here.')} `}
          {state === 'empty' && (
            <a href={`https://x.com/${screenName}`} target="_blank" rel="noopener noreferrer">
              {t('Open on X')}
            </a>
          )}
        </p>
      )}
    </article>
  );
}

/**
 * The Twitter feed — live's, with its "Add/Remove Profiles" dialog folded into
 * the panel: the list of followed profiles is edited in place and saved in one
 * go, as live's dialog saves it.
 */
export default function TwitterFeed() {
  const { data, isLoading } = useGetTwitterProfilesQuery();
  const [save, { isLoading: saving }] = useUpdateTwitterProfilesMutation();
  const saved = data?.data ?? [];

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [entry, setEntry] = useState('');

  const startEditing = () => {
    setDraft(saved);
    setEntry('');
    setEditing(true);
  };

  const add = (event) => {
    event.preventDefault();
    const handle = handleOf(entry);
    if (!VALID_HANDLE.test(handle)) {
      message.error(t('Enter an X handle, like @AlphaCapital'));
      return;
    }
    if (!draft.some((name) => name.toLowerCase() === handle.toLowerCase())) {
      setDraft([...draft, handle]);
    }
    setEntry('');
  };

  const commit = async () => {
    try {
      await save(draft).unwrap();
      setEditing(false);
    } catch {
      message.error(t('Could not save your profiles'));
    }
  };

  return (
    <div className="tm-tweets">
      <div className="tm-tweets__bar">
        <span className="tm-tweets__count">
          {isLoading
            ? t('Loading profiles…')
            : saved.length === 1
              ? t('{{n}} profile followed', { n: saved.length })
              : t('{{n}} profiles followed', { n: saved.length })}
        </span>
        {!editing && (
          <Button variant="toolbar" onClick={startEditing} disabled={isLoading}>
            {t('Add / remove profiles')}
          </Button>
        )}
      </div>

      {editing && (
        <div className="tm-tweets__editor">
          <ul className="tm-tweets__chips" aria-label={t('Profiles to follow')}>
            {draft.map((name) => (
              <li key={name} className="tm-tweets__chip">
                @{name}
                <button
                  type="button"
                  aria-label={t('Remove @{{name}}', { name })}
                  onClick={() => setDraft(draft.filter((n) => n !== name))}
                >
                  ×
                </button>
              </li>
            ))}
            {draft.length === 0 && <li className="tm-tweets__chip-empty">{t('No profiles yet')}</li>}
          </ul>
          <form className="tm-tweets__add" onSubmit={add}>
            <input
              className="tm-tweets__input"
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              placeholder={t('@handle or x.com link')}
              aria-label={t('X handle to add')}
            />
            <Button variant="toolbar" htmlType="submit" disabled={!entry.trim()}>
              {t('Add')}
            </Button>
          </form>
          <div className="tm-tweets__actions">
            <Button variant="toolbar" onClick={() => setEditing(false)} disabled={saving}>
              {t('Cancel')}
            </Button>
            <Button variant="pill" height={32} paddingInline={16} onClick={commit} loading={saving}>
              {t('Save changes')}
            </Button>
          </div>
        </div>
      )}

      {!isLoading && saved.length === 0 && !editing && (
        <p className="tm-empty">
          {t('You aren’t following any profiles yet — add the accounts you want to see posts from.')}
        </p>
      )}

      <div className="tm-tweets__grid">
        {saved.map((name) => (
          <Timeline key={name} screenName={name} />
        ))}
      </div>
    </div>
  );
}
